import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { JobDetail } from '@jobos/shared/types/job';
import type { JobFileEntry } from '@jobos/shared';
import { formatClientName } from '@jobos/shared';
import { JobStatusBadge } from '@/components/shared/JobStatusBadge';
import { JobCardStatusBadge } from '@/components/shared/JobCardStatusBadge';
import { InvoiceStatusBadge } from '@/components/shared/InvoiceStatusBadge';
import { BillingBar } from '@/components/billing/BillingBar';
import { SortableTableHeader } from '@/components/shared/SortableTableHeader';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { JobPhase } from './JobPhaseSelector';
import { JobActionsDropdown } from './JobActionsDropdown';
import { JobPhotosCell } from './JobPhotosCell';
import { useScrollRestoration } from '@/hooks/useScrollRestoration';
import { JobFileLightbox } from './files/JobFileLightbox';
import { HIGHLIGHT_COLOR_MAP } from '../utils/highlightColors';
import {
  formatInvoiceDisplay,
  getDaysSinceInvoiceSent,
  getStatusDateWithLabel,
} from '../utils/jobListFormatters';

interface JobListTableProps {
  jobs: JobDetail[];
  selectedPhase: JobPhase;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  onSort: (field: string) => void;
  onRowClick: (jobId: string) => void;
}

export function JobListTable({
  jobs,
  selectedPhase,
  sortField,
  sortDirection,
  onSort,
  onRowClick,
}: JobListTableProps) {
  // One viewer for the whole table, driven by whichever row was clicked — which
  // is why the cell hands its photos up rather than rendering a lightbox of its
  // own, one per row.
  const [viewer, setViewer] = useState<{
    jobId: string;
    photos: JobFileEntry[];
    index: number;
  } | null>(null);

  // Coming back from a job returns the table to where it was left. The offset
  // lives on the wrapper, not the window — the height cap below is what makes
  // the wrapper the scroller.
  const scrollRef = useScrollRestoration({ name: 'job-list-table', ready: jobs.length > 0 });

  return (
    <div className="hidden md:block bg-white shadow rounded-lg">
      <Table wrapperClassName="max-h-[calc(100vh-12rem)]" wrapperRef={scrollRef}>
        <TableHeader className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_theme(colors.gray.200)]">
          <TableRow>
            <SortableTableHeader
              field="status"
              currentSortField={sortField}
              currentSortDirection={sortDirection}
              onSort={onSort}
            >
              Job Status
            </SortableTableHeader>
            <SortableTableHeader
              field="statusDate"
              currentSortField={sortField}
              currentSortDirection={sortDirection}
              onSort={onSort}
            >
              Date
            </SortableTableHeader>
            <SortableTableHeader
              field="jobCardStatus"
              currentSortField={sortField}
              currentSortDirection={sortDirection}
              onSort={onSort}
            >
              Job Card
            </SortableTableHeader>
            <SortableTableHeader
              field="invoiceStatus"
              currentSortField={sortField}
              currentSortDirection={sortDirection}
              onSort={onSort}
            >
              Invoice
            </SortableTableHeader>
            <SortableTableHeader
              field="invoiceNumber"
              currentSortField={sortField}
              currentSortDirection={sortDirection}
              onSort={onSort}
            >
              Invoice #
            </SortableTableHeader>
            {/* Not sortable: ordering jobs by how many photos they have is not
                a question anyone asks. */}
            <TableHead>Photos</TableHead>
            <SortableTableHeader
              field="jobCode"
              currentSortField={sortField}
              currentSortDirection={sortDirection}
              onSort={onSort}
            >
              Job Code
            </SortableTableHeader>
            <SortableTableHeader
              field="client"
              currentSortField={sortField}
              currentSortDirection={sortDirection}
              onSort={onSort}
            >
              Client
            </SortableTableHeader>
            <SortableTableHeader
              field="location"
              currentSortField={sortField}
              currentSortDirection={sortDirection}
              onSort={onSort}
            >
              Service Address
            </SortableTableHeader>
            <SortableTableHeader
              field="projectType"
              currentSortField={sortField}
              currentSortDirection={sortDirection}
              onSort={onSort}
            >
              Project Type
            </SortableTableHeader>
            {(selectedPhase === 'all' || selectedPhase === 'other') && (
              <SortableTableHeader
                field="description"
                currentSortField={sortField}
                currentSortDirection={sortDirection}
                onSort={onSort}
              >
                Description
              </SortableTableHeader>
            )}
            {selectedPhase === 'quoting' && (
              <SortableTableHeader
                field="noticeDate"
                currentSortField={sortField}
                currentSortDirection={sortDirection}
                onSort={onSort}
              >
                Notice Date
              </SortableTableHeader>
            )}
            {selectedPhase === 'building' && (
              <SortableTableHeader
                field="startDate"
                currentSortField={sortField}
                currentSortDirection={sortDirection}
                onSort={onSort}
              >
                Start Date
              </SortableTableHeader>
            )}
            {selectedPhase === 'billing' && (
              <>
                <SortableTableHeader
                  field="completedDate"
                  currentSortField={sortField}
                  currentSortDirection={sortDirection}
                  onSort={onSort}
                >
                  Completed Date
                </SortableTableHeader>
                <SortableTableHeader
                  field="cashInvoice"
                  currentSortField={sortField}
                  currentSortDirection={sortDirection}
                  onSort={onSort}
                >
                  Cash Invoice
                </SortableTableHeader>
              </>
            )}
            <TableCell className="w-12"></TableCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {jobs && jobs.length > 0 ? (
            jobs.map((job) => {
              const highlight = job.highlightColor
                ? HIGHLIGHT_COLOR_MAP[job.highlightColor as keyof typeof HIGHLIGHT_COLOR_MAP]
                : undefined;
              return (
              <TableRow
                key={job.id}
                onClick={() => onRowClick(job.id)}
                className={`cursor-pointer ${
                  highlight
                    ? 'bg-[var(--job-highlight-bg)] hover:bg-[var(--job-highlight-hover)]'
                    : 'hover:bg-gray-50'
                }`}
                style={
                  highlight
                    ? ({
                        '--job-highlight-bg': highlight.bg,
                        '--job-highlight-hover': highlight.hover,
                      } as React.CSSProperties)
                    : undefined
                }
              >
                <TableCell>
                  <JobStatusBadge status={job.status} noticeDate={job.noticeDate} />
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {(() => {
                    const dateInfo = getStatusDateWithLabel(job);
                    if (!dateInfo) return <span className="text-gray-400">-</span>;
                    const date = new Date(dateInfo.date);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const dateOnly = new Date(date);
                    dateOnly.setHours(0, 0, 0, 0);
                    const diffDays = Math.floor((today.getTime() - dateOnly.getTime()) / (1000 * 60 * 60 * 24));
                    const daysText = diffDays === 0 ? 'today' : diffDays === 1 ? '1d' : `${diffDays}d`;
                    return (
                      <div className="flex flex-col">
                        <span className="text-[10px] text-gray-400">{dateInfo.label}</span>
                        <span className="text-xs text-gray-700">{date.toLocaleDateString()}</span>
                        <span className="text-[11px] text-gray-500 font-medium">{daysText}</span>
                      </div>
                    );
                  })()}
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  <JobCardStatusBadge status={job.jobCardStatus} />
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  <div className="flex flex-col gap-[5px] min-w-[120px]">
                    <InvoiceStatusBadge
                      status={job.invoiceStatus}
                      lastInvoicedDate={job.lastInvoicedDate}
                      billingType={job.billingType}
                    />
                    <BillingBar summary={job.jobBillingSummary} jobStatus={job.status} />
                  </div>
                </TableCell>
                <TableCell className="text-xs text-gray-600 whitespace-nowrap">
                  {formatInvoiceDisplay(job.invoiceNumber, job.billingType)}
                  {getDaysSinceInvoiceSent(job.lastInvoicedDate, job.billingType, job.invoiceStatus) && (
                    <span className="text-[11px] text-gray-500 font-medium"> ({getDaysSinceInvoiceSent(job.lastInvoicedDate, job.billingType, job.invoiceStatus)})</span>
                  )}
                </TableCell>
                {/* No cell-level stopPropagation: JobPhotosCell stops it on the
                    control itself, so a click on the cell's dead space (the
                    whole cell on a job with no photos) still opens the job,
                    like every other cell in the row. */}
                <TableCell className="whitespace-nowrap">
                  <JobPhotosCell
                    jobId={job.id}
                    photoCount={job.photoCount}
                    onOpenViewer={(photos, index) => setViewer({ jobId: job.id, photos, index })}
                  />
                </TableCell>
                <TableCell className="font-mono text-sm text-blue-600 whitespace-nowrap">
                  {job.jobCode || '-'}
                </TableCell>
                <TableCell className="text-sm font-medium">
                  <Link
                    to={`/clients/${job.clientId}`}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    {formatClientName(job.client.name, job.client.firstName, job.client.lastName)}
                  </Link>
                </TableCell>
                <TableCell className="text-sm">
                  {job.location.addressLine1}
                  {job.location.addressLine2 && `, ${job.location.addressLine2}`}
                </TableCell>
                <TableCell className="text-sm">
                  {job.projectType === 'Other' && job.customProjectType
                    ? job.customProjectType
                    : job.projectType}
                </TableCell>
                {(selectedPhase === 'all' || selectedPhase === 'other') && (
                  <TableCell className="text-sm max-w-xs truncate">
                    {job.description || '-'}
                  </TableCell>
                )}
                {selectedPhase === 'quoting' && (
                  <TableCell className="text-sm">
                    {job.noticeDate
                      ? new Date(job.noticeDate).toLocaleDateString()
                      : '-'}
                  </TableCell>
                )}
                {selectedPhase === 'building' && (
                  <TableCell className="text-sm">
                    {job.startDate
                      ? new Date(job.startDate).toLocaleDateString()
                      : '-'}
                  </TableCell>
                )}
                {selectedPhase === 'billing' && (
                  <>
                    <TableCell className="text-sm">
                      {job.completedDate
                        ? new Date(job.completedDate).toLocaleDateString()
                        : '-'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {job.invoiceType === 'cash' ? 'Yes' : 'No'}
                    </TableCell>
                  </>
                )}
                <TableCell>
                  <JobActionsDropdown job={job} />
                </TableCell>
              </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell
                colSpan={
                  selectedPhase === 'billing' ? 12 :
                  (selectedPhase === 'all' || selectedPhase === 'other' || selectedPhase === 'quoting' || selectedPhase === 'building') ? 11 :
                  10
                }
                className="text-center py-8"
              >
                <div className="text-gray-500">
                  <p className="text-lg font-medium">No jobs found</p>
                  <p className="text-sm mt-1">
                    Get started by creating your first job
                  </p>
                  <Link to="/jobs/new">
                    <Button className="mt-4">Create New Job</Button>
                  </Link>
                </div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      {viewer && (
        <JobFileLightbox
          jobId={viewer.jobId}
          files={viewer.photos}
          index={viewer.index}
          onIndexChange={(index) => setViewer((v) => v && { ...v, index })}
          onClose={() => setViewer(null)}
        />
      )}
    </div>
  );
}
